import { getLangFont } from "../utils/helpers";
import { FEATURE_CARDS, WHY_HEADING, WHY_SUBTITLE, WA_SUBTITLE } from "../data/constants";

/**
 * HomePage
 * The landing page with a hero section, stats bar, feature cards,
 * and a WhatsApp CTA banner.
 *
 * Props:
 *   lang  {string}   – active language code
 *   t     {object}   – translations object
 *   onNav {function} – navigate to another page
 */
export default function HomePage({ lang, t, onNav }) {
  const bf = getLangFont(lang);

  return (
    <div>
      {/* ── Hero ────────────────────────────────────────────── */}
      <section
        style={{
          background: "linear-gradient(135deg,#1a4d2e 0%,#2d6a4f 45%,#40916c 100%)",
          padding:    "80px 24px 64px",
          textAlign:  "center",
          position:   "relative",
          overflow:   "hidden",
        }}
      >
        {/* Subtle grain overlay */}
        <div
          style={{
            position:        "absolute",
            inset:           0,
            opacity:         0.05,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5L35 20L50 20L38 30L43 45L30 35L17 45L22 30L10 20L25 20Z' fill='%23fff'/%3E%3C/svg%3E")`,
            backgroundSize:  "60px 60px",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 700, margin: "0 auto" }}>
          {/* Live badge */}
          <div
            style={{
              display:        "inline-block",
              background:     "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              border:         "1px solid rgba(255,255,255,0.3)",
              borderRadius:   100,
              padding:        "6px 18px",
              fontSize:       12,
              color:          "#d4f1e4",
              marginBottom:   20,
              letterSpacing:  1,
              fontFamily:     "'DM Sans', sans-serif",
            }}
          >
            🌾 LIVE · MANDI BHAV · {new Date().toLocaleDateString("en-IN")} -{" "}
            <b>Under development (test data only)</b>
          </div>

          <h1
            style={{
              fontFamily: getLangFont(lang),
              fontSize:   "clamp(34px,7vw,62px)",
              color:      "#fff",
              margin:     "0 0 12px",
              lineHeight: 1.2,
            }}
          >
            {t.heroTitle}
          </h1>

          <p
            style={{
              fontFamily: bf,
              fontSize:   "clamp(14px,2vw,18px)",
              color:      "rgba(255,255,255,0.8)",
              margin:     "0 0 40px",
              fontWeight: 300,
            }}
          >
            {t.heroSub}
          </p>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => onNav("rates")}
              style={{
                background:   "#e07b00",
                color:        "#fff",
                border:       "none",
                borderRadius: 50,
                padding:      "14px 32px",
                fontSize:     15,
                fontWeight:   600,
                cursor:       "pointer",
                fontFamily:   bf,
                boxShadow:    "0 4px 20px rgba(224,123,0,0.4)",
                transition:   "transform 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
              onMouseLeave={(e) => (e.target.style.transform = "")}
            >
              📊 {t.navRates}
            </button>
              
            <button
              //onClick={() => onNav("register")}
              style={{
                background:     "rgba(255,255,255,0.15)",
                color:          "#fff",
                border:         "1px solid rgba(255,255,255,0.4)",
                borderRadius:   50,
                padding:        "14px 32px",
                fontSize:       15,
                fontWeight:     500,
                cursor:         "pointer",
                fontFamily:     bf,
                backdropFilter: "blur(10px)",
              }}
            >
              📲 {t.whatsapp}
            </button>
          </div>
        </div>
      </section>

      {/* ── Stats bar ───────────────────────────────────────── */}
      <section style={{ background: "#e07b00", padding: "14px 24px" }}>
        <div
          style={{
            maxWidth:       1100,
            margin:         "0 auto",
            display:        "flex",
            gap:            32,
            justifyContent: "center",
            flexWrap:       "wrap",
            fontFamily:     bf,
          }}
        >
          {[
            ["🏪", "500+",  t.mandi],
            ["🌾", "14+",   t.commodity],
            //["👨‍🌾", "25K+", t.navReg],
            //["📱", "Daily 7AM", "WhatsApp"],
          ].map(([icon, val, label]) => (
            <div key={label} style={{ textAlign: "center", color: "#fff" }}>
              <div style={{ fontSize: 20 }}>{icon}</div>
              <div style={{ fontSize: 19, fontWeight: 700 }}>{val}</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Feature cards ───────────────────────────────────── */}
      <section style={{ padding: "60px 24px", background: "#faf7f0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily:   getLangFont(lang),
              textAlign:    "center",
              fontSize:     "clamp(22px,4vw,36px)",
              color:        "#1a4d2e",
              marginBottom: 8,
            }}
          >
            {WHY_HEADING[lang] ?? WHY_HEADING.hi}
          </h2>
          <p
            style={{
              textAlign:    "center",
              color:        "#6b7280",
              marginBottom: 44,
              fontSize:     14,
              fontFamily:   bf,
            }}
          >
            {WHY_SUBTITLE[lang] ?? WHY_SUBTITLE.hi}
          </p>

          <div
            style={{
              display:             "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              gap:                 20,
            }}
          >
            {[0, 2, 3].map((i) => {
              const titles = FEATURE_CARDS.titles[lang] ?? FEATURE_CARDS.titles.hi;
              const descs  = FEATURE_CARDS.descs[lang]  ?? FEATURE_CARDS.descs.hi;
              const f = {
                icon:  FEATURE_CARDS.icon[i],
                title: titles[i],
                desc:  descs[i],
              };
              return (
                <div
                  key={i}
                  style={{
                    background:   "#fff",
                    borderRadius: 16,
                    padding:      24,
                    border:       "1px solid #e8e0d0",
                    transition:   "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <div style={{ fontSize: 34, marginBottom: 10 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: getLangFont(lang), fontSize: 18, color: "#1a4d2e", marginBottom: 6 }}>
                    {f.title}
                  </h3>
                  <p style={{ fontFamily: bf, color: "#6b7280", fontSize: 14, lineHeight: 1.6 }}>
                    {f.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WhatsApp CTA banner ─────────────────────────────── */}
      <section
        style={{
          background: "linear-gradient(135deg,#e07b00,#c86000)",
          padding:    "56px 24px",
          textAlign:  "center",
        }}
      >
        <h2
          style={{
            fontFamily:   getLangFont(lang),
            fontSize:     "clamp(22px,5vw,38px)",
            color:        "#fff",
            marginBottom: 10,
          }}
        >
          📲 {t.whatsapp}
        </h2>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, marginBottom: 28, fontFamily: bf }}>
          {WA_SUBTITLE[lang] ?? WA_SUBTITLE.hi}
        </p>
        <button
          //onClick={() => onNav("register")}
          style={{
            background:   "#fff",
            color:        "#e07b00",
            border:       "none",
            borderRadius: 50,
            padding:      "14px 38px",
            fontSize:     16,
            fontWeight:   700,
            cursor:       "pointer",
            fontFamily:   bf,
            boxShadow:    "0 4px 20px rgba(0,0,0,0.2)",
          }}
        >
          {t.submitReg}
        </button>
      </section>
    </div>
  );
}